import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Copy, Trash2, Edit3, ArrowLeft, History, CheckCircle2 } from "lucide-react";
import {
  PROFILE_KINDS, PROFILE_KIND_LABELS, PROFILE_KIND_HELPER,
} from "@/lib/profiles/schemas";
import {
  listProfiles, cloneProfile, deleteProfile, getHistory,
} from "@/lib/profiles/storage";

// ProfileList — generic list page that lists, clones, and deletes profiles
// of a single kind. Driven by the :kind URL param.

function _fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ProfileList() {
  const { kind } = useParams();
  const nav = useNavigate();
  const [refreshTick, setRefreshTick] = useState(0);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const valid = PROFILE_KINDS.includes(kind);
  const rows = useMemo(
    () => (valid ? listProfiles(kind) : []),
    [kind, valid, refreshTick],
  );

  // One-shot success flash written by ProfileEditor.handleSave().
  // Auto-clears after 6 s; survives a single page refresh.
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!valid) return;
    try {
      const raw = sessionStorage.getItem(`rsc.profiles.flash.${kind}`);
      if (raw) {
        const f = JSON.parse(raw);
        setFlash(f);
        sessionStorage.removeItem(`rsc.profiles.flash.${kind}`);
        const t = setTimeout(() => setFlash(null), 6000);
        return () => clearTimeout(t);
      }
    } catch { /* sessionStorage unavailable */ }
  }, [kind, valid]);

  if (!valid) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="profile-list-invalid">
        <p className="text-[var(--rs-text-2)]">
          Unknown profile kind: <code>{kind}</code>
        </p>
        <Link to="/profiles" className="text-[var(--rs-amber)] underline text-[12px]">
          ← Back to Profile Hub
        </Link>
      </div>
    );
  }

  const handleClone = (id) => {
    try {
      cloneProfile(kind, id);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      alert(`Clone failed: ${e.message}`);
    }
  };
  const handleDelete = (id) => {
    if (confirmDelete !== id) { setConfirmDelete(id); return; }
    try {
      deleteProfile(kind, id);
      setConfirmDelete(null);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  return (
    <div data-testid={`profile-list-${kind}`} className="max-w-3xl mx-auto px-4 py-6">
      <Link
        to="/profiles"
        data-testid="profile-list-back"
        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] mb-3"
      >
        <ArrowLeft className="w-3 h-3" /> Profile Hub
      </Link>

      <header className="flex items-baseline justify-between gap-2 mb-4">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-[var(--rs-text-1)]">
            {PROFILE_KIND_LABELS[kind]}
          </h1>
          <p className="text-[11px] text-[var(--rs-text-3)] leading-snug mt-0.5">
            {PROFILE_KIND_HELPER[kind]}
          </p>
        </div>
        <button
          data-testid={`profile-list-new-${kind}`}
          onClick={() => nav(`/profiles/${kind}/new`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
          style={{
            background: "var(--rs-amber, #f5b400)", color: "#0A0B0E",
            minHeight: 36,
          }}
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </header>

      {flash && (
        <div
          data-testid={`profile-list-flash-${kind}`}
          className="card-rs p-3 mb-3 flex items-start gap-2"
          style={{
            borderColor: "rgba(110, 231, 183, 0.45)",
            background: "rgba(110, 231, 183, 0.08)",
          }}
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#6ee7b7]" />
          <div className="text-[12px] leading-snug">
            <strong className="text-[#6ee7b7]">
              {flash.isNew ? "Profile saved." : "Profile updated."}
            </strong>{" "}
            <span className="text-[var(--rs-text-2)]">
              {flash.label} {flash.isNew ? "" : `· v${flash.version}`}
            </span>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div
          data-testid={`profile-list-empty-${kind}`}
          className="card-rs p-6 text-center"
        >
          <p className="text-[12px] text-[var(--rs-text-2)]">
            No {PROFILE_KIND_LABELS[kind].toLowerCase()} yet.
          </p>
          <p className="text-[11px] text-[var(--rs-text-3)] mt-1">
            Create one to use it on a new IWCF Kill Sheet later.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2" data-testid={`profile-list-rows-${kind}`}>
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`profile-list-row-${r.id}`}
              className="card-rs p-3"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <button
                    type="button"
                    data-testid={`profile-list-open-${r.id}`}
                    onClick={() => nav(`/profiles/${kind}/${r.id}`)}
                    className="text-[14px] font-semibold text-[var(--rs-text-1)] hover:text-[var(--rs-amber)] text-left"
                  >
                    {r.label || <span className="italic text-[var(--rs-text-3)]">(unnamed)</span>}
                  </button>
                  <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] mt-0.5">
                    v{r.version} · updated {_fmtDate(r.updatedAt)}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    data-testid={`profile-list-edit-${r.id}`}
                    onClick={() => nav(`/profiles/${kind}/${r.id}`)}
                    className="p-1.5 rounded text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]"
                    title="Edit"
                  ><Edit3 className="w-4 h-4" /></button>
                  <button
                    data-testid={`profile-list-clone-${r.id}`}
                    onClick={() => handleClone(r.id)}
                    className="p-1.5 rounded text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]"
                    title="Clone"
                  ><Copy className="w-4 h-4" /></button>
                  <button
                    data-testid={`profile-list-history-${r.id}`}
                    onClick={() => setExpandedHistory(expandedHistory === r.id ? null : r.id)}
                    className="p-1.5 rounded text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]"
                    title="Version history"
                  ><History className="w-4 h-4" /></button>
                  <button
                    data-testid={`profile-list-delete-${r.id}`}
                    onClick={() => handleDelete(r.id)}
                    className="p-1.5 rounded hover:text-[#FCA5A5]"
                    style={{ color: confirmDelete === r.id ? "#FCA5A5" : "var(--rs-text-2)" }}
                    title={confirmDelete === r.id ? "Click again to confirm" : "Delete"}
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {expandedHistory === r.id && (
                <div
                  data-testid={`profile-list-history-panel-${r.id}`}
                  className="mt-2 pt-2 border-t border-[var(--rs-border)] text-[10.5px] text-[var(--rs-text-3)]"
                >
                  <div className="font-mono uppercase tracking-wider mb-1">Version history (oldest → newest)</div>
                  <ol className="flex flex-col gap-0.5">
                    {getHistory(kind, r.id).slice().reverse().map((h) => (
                      <li key={h.version} className="flex justify-between">
                        <span>v{h.version}</span>
                        <span className="font-mono">{_fmtDate(h.updatedAt)}</span>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-2 text-[10px] italic">
                    Older versions are preserved so historical kill sheets that
                    referenced them keep resolving correctly.
                  </p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
